import type { Lang, LocalisedString } from '../config/types'

/**
 * Per-salon override block stored in `content.json` (PRJT-134, Slice 1).
 *
 * EVERY field is optional. When a field is absent (or fails validation) the
 * website falls back to the in-code value in `src/config/salons.ts`, so an
 * empty / missing / malformed `content.json` renders the site EXACTLY as the
 * hard-coded defaults - see `resolveSalon` in `src/lib/salons.ts`.
 *
 * Slice 2 (PRJT-134) adds `contact` + `holidayHours` at the top level (see
 * {@link ContactOverride} / {@link HolidayHours}); both are OPTIONAL and fall
 * back to the in-code `siteConfig` (contact) / render nothing (holiday hours).
 */
export type SalonOverride = Partial<{
  address: string
  phone: string
  bookingUrl: string
  /**
   * Parking, per language. May be partial (just `en` or just `da`) - the
   * website merges it over the in-code parking so BOTH languages always
   * resolve (see `resolveSalon`).
   */
  parking: Partial<LocalisedString>
  /** When true the salon shows a "Temporarily closed" notice on its pages. */
  temporarilyClosed: boolean
  hours: { weekdays: string; saturday: string; sunday: string }
}>

/**
 * Global contact override stored in `content.json` (PRJT-134, Slice 2).
 *
 * EVERY field is optional. When a field is absent (or fails validation) the
 * website falls back to the in-code value in `src/config/site.ts`
 * (`siteConfig.contact.email`, `siteConfig.social.*`) - see `resolveContact`
 * in `src/lib/site.ts`. An empty / missing block renders the site EXACTLY as
 * the hard-coded defaults.
 */
export type ContactOverride = Partial<{
  email: string
  phone: string
  instagram: string
  facebook: string
  tiktok: string
  youtube: string
}>

/**
 * A single holiday-hours entry stored in `content.json` (PRJT-134, Slice 2).
 *
 * `date` (ISO "YYYY-MM-DD") is the only required field. `label` is an optional
 * partial {en?, da?} description (e.g. "Christmas Eve"). An entry is EITHER
 * `closed: true` OR carries an `hours` string (e.g. "10:00-14:00"); when both
 * are absent the notice simply shows the date + label. Entries without a usable
 * `date` are dropped at sanitisation time.
 */
export interface HolidayHours {
  date: string
  label?: Partial<LocalisedString>
  closed?: boolean
  hours?: string
}

/**
 * The shape of `content.json`. Permissive by design: it is a SEPARATE document
 * from `runtime.json`, owned by the concierge content editor (PRJT-133's
 * `chatKnowledge`) and extended here with per-salon overrides. Unknown / Phase 2
 * fields are preserved by the admin SPA round-trip and ignored by the site.
 */
export interface ContentConfig {
  version?: number
  /** Markdown - the concierge knowledge base (PRJT-133). Read-through only here. */
  chatKnowledge?: string
  /** Global contact override (email / phone / instagram / facebook / tiktok / youtube). PRJT-134 Slice 2. */
  contact?: ContactOverride
  /** Site-wide holiday opening hours, in display order. PRJT-134 Slice 2. */
  holidayHours?: HolidayHours[]
  /** Per-salon overrides keyed by salon id (vanlose, vaerlose, …). */
  salons?: Record<string, SalonOverride>
}

let current: ContentConfig = {}
let loaded = false
let loadPromise: Promise<void> | null = null

export function getContentConfig(): ContentConfig {
  return current
}

export function isContentConfigLoaded(): boolean {
  return loaded
}

export function setContentConfigForTesting(config: ContentConfig): void {
  current = config
  loaded = true
}

export function resetContentConfigForTesting(): void {
  current = {}
  loaded = false
  loadPromise = null
}

// Exported for testability: lets tests assert dev-only logging without
// mucking with the read-only import.meta.env.DEV flag. Mirrors runtimeConfig.
export function devWarn(message: string, error?: unknown, isDev = import.meta.env.DEV): void {
  if (!isDev) return
  console.warn(`[contentConfig] ${message}`, error ?? '')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const PARKING_LANGS: readonly Lang[] = ['en', 'da']

/**
 * Validates the parking override - an object with `en` / `da` non-empty string
 * entries. Only the two supported languages are kept; non-string / empty / stray
 * keys are dropped. Returns the cleaned partial map, or null if nothing usable
 * remains. The website merges this over the in-code parking so both languages
 * still resolve even when only one is overridden.
 */
function sanitizeParking(raw: unknown): Partial<LocalisedString> | null {
  if (!isPlainObject(raw)) return null
  const out: Partial<LocalisedString> = {}
  for (const lang of PARKING_LANGS) {
    const v = raw[lang]
    if (typeof v === 'string' && v.length > 0) out[lang] = v
  }
  return Object.keys(out).length > 0 ? out : null
}

function sanitizeHours(raw: unknown): SalonOverride['hours'] | undefined {
  if (!isPlainObject(raw)) return undefined
  const out: { weekdays?: string; saturday?: string; sunday?: string } = {}
  if (typeof raw.weekdays === 'string' && raw.weekdays.length > 0) out.weekdays = raw.weekdays
  if (typeof raw.saturday === 'string' && raw.saturday.length > 0) out.saturday = raw.saturday
  if (typeof raw.sunday === 'string' && raw.sunday.length > 0) out.sunday = raw.sunday
  // Hours is a fixed-shape object - only return it when all three are present so
  // a half-filled override never produces a partial hours row.
  if (out.weekdays && out.saturday && out.sunday) {
    return { weekdays: out.weekdays, saturday: out.saturday, sunday: out.sunday }
  }
  return undefined
}

function sanitizeSalonOverride(raw: unknown): SalonOverride | null {
  if (!isPlainObject(raw)) return null
  const out: SalonOverride = {}
  if (typeof raw.address === 'string' && raw.address.length > 0) out.address = raw.address
  if (typeof raw.phone === 'string' && raw.phone.length > 0) out.phone = raw.phone
  if (typeof raw.bookingUrl === 'string' && raw.bookingUrl.length > 0) out.bookingUrl = raw.bookingUrl

  const parking = sanitizeParking(raw.parking)
  if (parking) out.parking = parking

  if (typeof raw.temporarilyClosed === 'boolean') out.temporarilyClosed = raw.temporarilyClosed

  const hours = sanitizeHours(raw.hours)
  if (hours) out.hours = hours

  return Object.keys(out).length > 0 ? out : null
}

const CONTACT_FIELDS: readonly (keyof ContactOverride)[] = ['email', 'phone', 'instagram', 'facebook', 'tiktok', 'youtube']

/**
 * Validates the global contact override - an object of non-empty string fields
 * (email / phone / instagram / facebook / tiktok / youtube). Unknown keys and non-string / empty
 * values are dropped. Returns the cleaned partial, or null when nothing usable
 * remains (so the website keeps the in-code `siteConfig` defaults).
 */
function sanitizeContact(raw: unknown): ContactOverride | null {
  if (!isPlainObject(raw)) return null
  const out: ContactOverride = {}
  for (const field of CONTACT_FIELDS) {
    const v = raw[field]
    if (typeof v === 'string' && v.length > 0) out[field] = v
  }
  return Object.keys(out).length > 0 ? out : null
}

/**
 * Validates a single holiday-hours entry. `date` must be a non-empty string
 * (the entry is dropped otherwise). `label` is sanitised via the same
 * localized-string helper used for parking ({en?, da?} only). `closed` is kept
 * only when a real boolean; `hours` only when a non-empty string. Returns the
 * cleaned entry, or null when the date is missing/invalid.
 */
function sanitizeHolidayEntry(raw: unknown): HolidayHours | null {
  if (!isPlainObject(raw)) return null
  if (typeof raw.date !== 'string' || raw.date.length === 0) return null

  const entry: HolidayHours = { date: raw.date }

  const label = sanitizeParking(raw.label)
  if (label) entry.label = label

  if (typeof raw.closed === 'boolean') entry.closed = raw.closed
  if (typeof raw.hours === 'string' && raw.hours.length > 0) entry.hours = raw.hours

  return entry
}

/**
 * Validates the holiday-hours array: keeps only entries with a usable `date`,
 * each individually sanitised. A non-array input, or an array with no surviving
 * entry, yields null so the `holidayHours` key is omitted and no notice renders.
 */
function sanitizeHolidayHours(raw: unknown): HolidayHours[] | null {
  if (!Array.isArray(raw)) return null
  const out: HolidayHours[] = []
  for (const item of raw) {
    const clean = sanitizeHolidayEntry(item)
    if (clean) out.push(clean)
  }
  return out.length > 0 ? out : null
}

function sanitize(raw: unknown): ContentConfig {
  if (!isPlainObject(raw)) return {}
  const out: ContentConfig = {}

  if (typeof raw.version === 'number') out.version = raw.version
  if (typeof raw.chatKnowledge === 'string') out.chatKnowledge = raw.chatKnowledge

  const contact = sanitizeContact(raw.contact)
  if (contact) out.contact = contact

  const holidayHours = sanitizeHolidayHours(raw.holidayHours)
  if (holidayHours) out.holidayHours = holidayHours

  if (isPlainObject(raw.salons)) {
    const salons: Record<string, SalonOverride> = {}
    for (const [id, override] of Object.entries(raw.salons)) {
      const clean = sanitizeSalonOverride(override)
      if (clean) salons[id] = clean
    }
    if (Object.keys(salons).length > 0) out.salons = salons
  }

  return out
}

/**
 * Loads `content.json` once. Subsequent calls return the cached promise.
 * Failure is silent for users (logged in dev only): the site renders from the
 * in-code salon defaults. Mirrors loadRuntimeConfig.
 */
export async function loadContentConfig(): Promise<void> {
  if (loadPromise) return loadPromise

  const urlEnv = (import.meta.env.VITE_CONTENT_CONFIG_URL as string | undefined)?.trim()
  const url = urlEnv && urlEnv.length > 0 ? urlEnv : '/config/content.json'

  loadPromise = (async () => {
    try {
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) {
        devWarn(`fetch failed with status ${res.status} for ${url}`)
        return
      }
      // The SPA catch-all returns 200 HTML when the file doesn't exist yet.
      // Treat a non-JSON response as "not deployed" - fall back silently.
      const ct = res.headers?.get('content-type') ?? ''
      if (ct && !ct.includes('application/json') && !ct.includes('text/json')) {
        devWarn(`skipping non-JSON response for ${url} (content-type: ${ct})`)
        return
      }
      const json = (await res.json()) as unknown
      current = sanitize(json)
    } catch (err) {
      devWarn(`fetch threw for ${url}`, err)
    } finally {
      loaded = true
    }
  })()

  return loadPromise
}
